import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import { UserDto } from 'api/users/models/UserDto';
import { UserSearchCriteria } from 'api/users/models/UserSearchCriteria';
import Loading from 'common/services/Loading';
import UsersService from 'api/users/UsersService';
import Logger from 'common/services/Logger';
import styles from './UsersList.module.scss';
import InputSearch from 'common/components/inputSearch/InputSearch';
import { useDebouncedCallback } from 'use-debounce';
import DropdownItem from 'common/components/popover/PopoverItem';
import { FaEllipsisH } from 'react-icons/fa';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import Dropdown from 'common/components/dropdown/Dropdown';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import Popover from 'common/components/popover/Popover';
import UsersFiltersScreen, { UserFilters } from 'screens/security/components/usersTab/components/usersFilters/UsersFiltersScreen';
import AvatarWithText from 'common/components/avatar/AvatarWithText';
import { useHistory } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { RoleDto } from 'api/roles/models/RoleDto';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';

type Props = {
};

const UsersList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();

    const [users, setUsers] = useState<UserDto[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [criteria, setCriteria] = useState<UserSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'u.real_name'
    });
    const [filters, setFilters] = useState<UserFilters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<UserDto | null>(null);
    const isFirstLoad = useRef(true);
    const [reRenderSearch, setReRenderSearch] = useState<number>(0);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [order, setOrder] = useState<OrderData<UserDto>>({
        field: 'realName',
        orderColumn: 'u.real_name',
        isOrderAsc: true,
    });

    const bidirectionalScroll = useBidirectionalScroll({
        cacheKey: 'USERSFILTER',
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });
    const {
        loadedPageRange, isLoadingPrevious, scrollToRowId,
        setLoadedPageRange, setIsLoadingPrevious,
        resetScrollState, prepareLoadPrevious, adjustScrollAfterPrepend,
        finishRestoration, initiateRestoration, calculatePageFromIndex,
        shouldLoadAdjacentPages,
        getHasMore, getHasMorePrevious, getOnLoadMore, getOnLoadPrevious,
    } = bidirectionalScroll;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasUsersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_USERS_WRITE']);

    const showRemoveItemDialog = async (item: UserDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
            return;
        }

        try {
            await UsersService.remove(itemToRemove);
            setUsers([]);
            resetScrollState();
            setTotalItems(0);
            setCriteria(prev => ({ ...prev, page: 1 }));
            onCancelRemove();
            addToast(t('common.messages.record_delete_success'), {
                appearance: 'success',
            });
        }
        catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't delete user`, error);
            addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
        }
    };

    const renderTableActionCell = (row: UserDto) => {
        return (
            <Dropdown
                options={
                    <>
                        <DropdownItem url={`/security/users/details/${row.id}`}>
                            {t('common.details')}
                        </DropdownItem>
                        {hasUsersWritePolicy && <DropdownItem url={`/security/users/edit/${row.id}`}>
                            {t('common.edit')}
                        </DropdownItem>}
                        {hasUsersWritePolicy && !row.isLastAdmin && <DropdownItem onClick={() => showRemoveItemDialog(row)}>
                            {t('common.remove')}
                        </DropdownItem>}
                    </>
                }
            >
                <div>
                    <FaEllipsisH />
                </div>
            </Dropdown>
        );
    };

    const dataTableColumns = useMemo<DataTableColumn<UserDto>[]>(() => [
        {
            field: 'realName',
            name: t('users.list.name') as string,
            orderColumn: 'u.real_name',
            width: 300,
            exportValue: row => row.realName ?? '',
            renderCell: row => <AvatarWithText src={row.mediaUrl} size="x3"><span style={{ wordBreak: 'break-word', maxWidth: '90%' }}>{row.realName}</span></AvatarWithText>,
        },
        {
            field: 'email',
            name: t('users.list.email') as string,
            orderColumn: 'u.email',
            width: 300,
            renderCell: row => <span style={{ wordBreak: 'break-all' }}>{row.email}</span>,
        },
        {
            field: 'roles' as keyof UserDto,
            name: t('roles.title') as string,
            width: 300,
            hideOrderButton: true,
            exportValue: row => row.roles?.join(', ') ?? '',
            renderCell: row => <span style={{ wordBreak: 'break-all' }}>{row.roles?.join(', ')}</span>,
        },
    ], [t]);

    const fetchAllForExport = useCallback(async (): Promise<UserDto[]> => {
        const result = await UsersService.getList({ ...criteria, page: 1, itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE });
        const items = result.items || [];
        mapRoles(items);
        return items;
    }, [criteria]);

    const mapRoles = (items: UserDto[]) => {
        items.forEach(userObj => {
            if (userObj.temporaryRoles) {
                userObj.roles = userObj.temporaryRoles.map((role: RoleDto) => {
                    if (role.readOnly || role.system) {
                        return t(('common.roles.' + role.name) as any);
                    }
                    return role.name;
                });
            }
        });
    };

    const getData = async (isLoadMore = false) => {
        try {
            if (!isLoadMore) Loading.show();
            else setIsLoadingMore(true);

            saveOrdernationFilters();

            const page = await UsersService.getList(criteria);
            setTotalItems(page.totalItems);
            mapRoles(page.items);

            if (isLoadMore) {
                setUsers(prev => [...prev, ...page.items]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setUsers(page.items);
            }

            // Handle scroll restoration - load adjacent pages
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(page.totalItems / (criteria.itemsPerPage || DEFAULT_PAGINATION_ITEMS_PER_PAGE));

                if (currentPage > 1) {
                    try {
                        const prevResult = await UsersService.getList({ ...criteria, page: currentPage - 1 });
                        mapRoles(prevResult.items);
                        setUsers(prev => [...prevResult.items, ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load previous page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextResult = await UsersService.getList({ ...criteria, page: currentPage + 1 });
                        mapRoles(nextResult.items);
                        setUsers(prev => [...prev, ...nextResult.items]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't load next page`, e);
                    }
                }

                finishRestoration();
            }

            if (!isLoadMore) Loading.hide();
            else setIsLoadingMore(false);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get users list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
            setIsLoadingMore(false);
            resetScrollState();
        }
    };

    const loadMore = useCallback(() => {
        if (isLoadingMore || users.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, users.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;
        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;
        try {
            prepareLoadPrevious();
            const result = await UsersService.getList({ ...criteria, page: prevPage });
            mapRoles(result.items);
            setUsers(prev => [...result.items, ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria]);

    const handleOrderChange = (orderData: OrderData<UserDto>) => {
        setOrder(orderData);
        setUsers([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const saveFiltersOnCache = (f: UserFilters) => {
        setFilters(f);
        localStorage.setItem('USERSFILTER', JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem('USERSFILTER');
        if (cachedFilters != null) {
            const parsed = JSON.parse(cachedFilters) as UserFilters;

            if (parsed.scrollToUserId) {
                const startPage = initiateRestoration({
                    page: parsed.page || 1,
                    scrollToItemId: parsed.scrollToUserId,
                });
                const cleanedFilters = { ...parsed, scrollToUserId: undefined, page: 1 };
                localStorage.setItem('USERSFILTER', JSON.stringify(cleanedFilters));
                onChangeFilters(cleanedFilters, true);
                setCriteria(prev => ({ ...prev, page: startPage }));
            } else {
                parsed.scrollToUserId = undefined;
                parsed.page = 1;
                setLoadedPageRange({ min: 1, max: 1 });
                localStorage.setItem('USERSFILTER', JSON.stringify(parsed));
                onChangeFilters(parsed, true);
            }
        } else {
            getData();
        }

        isFirstLoad.current = false;
    }

    const onChangeFilters = (f: UserFilters, isOnLoad?: boolean) => {
        updateTotalFilters(f);
        saveFiltersOnCache(f);

        if (!isOnLoad) {
            setUsers([]);
            resetScrollState();
            setTotalItems(0);
        }

        const orderBy = f.orderBy || 'asc';
        const orderColumn = f.orderColumn || 'u.real_name';

        setCriteria({
            ...criteria,
            roles: f.roles?.map(r => r.value),
            page: (isOnLoad && f.page) ? f.page : 1,
            orderBy,
            orderColumn,
            allIn: f.allIn
        });

        const getFieldFromOrderColumn = (oc: string): keyof UserDto => {
            const mapping: Record<string, keyof UserDto> = {
                'u.real_name': 'realName',
                'u.email': 'email',
            };
            return mapping[oc] ?? 'realName';
        };

        setOrder({
            field: getFieldFromOrderColumn(orderColumn),
            orderColumn,
            isOrderAsc: orderBy === 'asc',
        });

        setReRenderSearch(r => r + 1);
    }

    const updateTotalFilters = (f: UserFilters) => {
        let total = 0;
        if (f.roles && f.roles.length > 0) {
            total++;
        }
        setFiltersTotal(total);
    }

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setUsers([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl === 'new') {
            canAddUsers();
        }
        history.push(`/security${typeUrl ? `/users/${typeUrl}${id ? `/${id}` : ''}` : ''}`);
    }

    const onRowClick = (row: UserDto, index: number, event: any) => {
        if (row.id) {
            const actualPage = calculatePageFromIndex(index);
            filters.page = actualPage;
            filters.scrollToUserId = row.id;
            saveFiltersOnCache(filters);

            if (event.ctrlKey) {
                window.open('/security/users/details/' + row.id, '_blank');
                return;
            }
            navigateTo('details', row.id);
        }
    }

    const saveOrdernationFilters = () => {
        filters.page = criteria.page;
        filters.orderBy = criteria.orderBy;
        filters.orderColumn = criteria.orderColumn;
        filters.allIn = criteria.allIn;

        saveFiltersOnCache(filters);
    }

    const canAddUsers = async () => {
        try {
            Loading.show();
            await UsersService.candAddUsers();
            Loading.hide();
        } catch (error) {
            if (error?.response?.status === 402) {
                addToast(t('common.errors.pack_limit_exceeded'), { appearance: 'warning' });
                history.push(`/account/details/subscription`);
            }
        }
    }

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters();
        } else {
            const isLoadMore = criteria.page > loadedPageRange.max && users.length > 0;
            getData(isLoadMore);
        }
    }, [criteria]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [users, isLoadingPrevious, adjustScrollAfterPrepend]);

    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];
        if (filters.roles && filters.roles.length > 0) {
            items.push({
                key: 'roles',
                label: t('roles.title') as string,
                value: filters.roles.map(r => r.label).join(', '),
                onRemove: () => onChangeFilters({ ...filters, roles: [] }),
            });
        }
        return items;
    }, [filters, t]);

    return (
        <ScreenTitle title={t('users.title')}>
            <CompactPageLayout
                title={t('users.title') as string}
                contentAreaClassName={styles.contentArea}
                middleContent={
                    <FilterBadgesOverflow items={filterItems} />
                }
                headerButtons={
                    <>
                        <InputSearch
                            key={reRenderSearch}
                            defaultValue={criteria.allIn}
                            onChangeValue={debounced}
                            placeholder={t('users.list.search_by')}
                        />
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => (
                                <UsersFiltersScreen
                                    filters={filters}
                                    onFilter={f => { setIsPopoverOpen(false); onChangeFilters(f); }}
                                    onChange={f => updateTotalFilters(f)}
                                />
                            )}
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 &&
                                        <div className={styles.counterList}>
                                            <div className={styles.counterNumber}>{filtersTotal}</div>
                                        </div>
                                    }
                                </ScreenHeaderButton>
                            )}
                        </Popover>
                        <ExportListingButton exportRef={exportListingRef} />
                        {hasUsersWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                    </>
                }
            >
                <div className={compactPageLayoutStyles.listTblWrapper}>
                    <DataTableWithSidebar
                        cacheKey="USERS_TABLE"
                        columns={dataTableColumns}
                        rows={users}
                        defaultOrder={order}
                        onOrderChange={handleOrderChange}
                        onRowClick={(row, index, event) => onRowClick(row, index, event)}
                        onRowId={row => row.id ?? ''}
                        onRenderAction={renderTableActionCell}
                        scrollToRowId={scrollToRowId}
                        exportRef={exportListingRef}
                        hideExportInSidebar={true}
                        exportExcel={{
                            fetchAll: fetchAllForExport,
                            fileName: t('users.title') as string,
                        }}
                        infiniteScroll={{
                            isLoading: isLoadingMore,
                            hasMore: getHasMore(users.length, totalItems),
                            onLoadMore: getOnLoadMore(loadMore),
                            hasMorePrevious: getHasMorePrevious(),
                            isLoadingPrevious,
                            onLoadPrevious: getOnLoadPrevious(loadPrevious),
                        }}
                    />
                </div>
                <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal}
                    message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.realName ?? '' })} />
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default UsersList;
